<template>
  <div class="page-wrap">

    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Expense Categories"
      subtitle="Manage categories for classifying expenses"
      add-label="Add"
      @add="openAddDialog"
      :columns="columns"
      :items="categories"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
    >
      <!-- Custom Cells -->
      <template #col-name="{ item }">
        <div class="category-name-cell">
          <span class="cat-name">{{ item.name }}</span>
          <span v-if="item.is_default" class="default-badge">System Default</span>
        </div>
      </template>

      <!-- Row Actions -->
      <template #actions="{ item }">
        <ActionButtons
          :show-edit="true"
          :show-delete="!item.is_default"
          @edit="openEditDialog(item)"
          @delete="confirmDeleteDialog(item.id)"
        />
      </template>
    </PremiumTable>

    <!-- ── Add / Edit Modal ────────────────── -->
    <PremiumModal
      v-model="addCategoryDialog"
      :title="(category.id ? 'Edit' : 'New') + ' Category'"
      :loading="saveLoading"
       submit-text="Submit"
      max-width="440"
      @submit="category.id ? updateCategory() : saveCategory()"
      @close="closeDialog"
    >
      <PremiumInput
        v-model="category.name"
        label="Category Name"
        placeholder="e.g. Maintenance, Electricity, etc."
        icon="mdi-tag-outline"
        v-validate="'required'"
        name="name"
        data-vv-as="Name"
        :error="errors.first('name') || serverSideErrors.name"
      />
    </PremiumModal>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Category"
      message="Are you sure you want to delete this category? This might affect existing records associated with it."
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteCategory"
    />

  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumModal from "../../common/PremiumModal.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import ActionButtons from "../../common/ActionButtons.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: 'CategoryList',
  mixins: [filterMixin],
  components: { PremiumTable, PremiumModal, PremiumInput, ConfirmModal, ActionButtons },

  data() {
    return {
      search:            "",
      categories:        [],
      loading:           false,
      saveLoading:       false,
      deleteLoading:     false,
      addCategoryDialog: false,
      deleteDialog:      false,
      delete_id:         null,
      page: 1,
      itemsPerPage: 10,
      totalRecords: 0,
      category:          { id: null, name: "" },
      serverSideErrors:  {},
      user:              {},
    };
  },

  computed: {
    ...mapState('theme', ['primaryColor']),

    columns() {
      return [
        { key: 'name', label: this.$t("category.headers.name") || 'Category Name' },
      ];
    },
  },

  watch: {
    search() {
      this.page = 1;
      this.getCategories();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
    },

    getCategories() {
      this.loading = true;
      let url = `societyadmin/categories?page=${this.page}&perPage=${this.itemsPerPage}`;
      if (this.search) url += `&search=${this.search}`;

      this.$axios.get(url).then(response => {
        this.loading = false;
        const data = response.data;
        if (data.status) {
          this.categories = data.categories.data || data.categories || [];
          
          // Fallback calculation for pagination if total is missing from backend
          let calculatedTotal = 0;
          if (data.lastPage) {
            calculatedTotal = data.page === data.lastPage 
              ? ((data.page - 1) * data.perPage) + this.categories.length 
              : data.lastPage * data.perPage;
          } else {
            calculatedTotal = this.categories.length;
          }
          
          this.totalRecords = data.total || (data.categories && data.categories.total) || calculatedTotal;
        } else {
          this.showError(data.message);
        }
      }).catch(() => { this.loading = false; });
    },

    saveCategory() {
      this.$validator.validateAll().then(success => {
        if (success) {
          this.saveLoading = true;
          this.$axios.post("societyadmin/categories", { name: this.category.name }).then(response => {
            this.saveLoading = false;
            const data = response.data;
            if (data.status) {
              this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: data.message });
              this.getCategories();
              this.closeDialog();
            } else {
              this.handleErrors(data);
            }
          }).catch(() => { this.saveLoading = false; });
        }
      });
    },

    updateCategory() {
      this.$validator.validateAll().then(success => {
        if (success) {
          this.saveLoading = true;
          this.$axios.put(`societyadmin/categories/${this.category.id}`, { name: this.category.name }).then(response => {
            this.saveLoading = false;
            const data = response.data;
            if (data.status) {
              this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: data.message });
              this.getCategories();
              this.closeDialog();
            } else {
              this.handleErrors(data);
            }
          }).catch(() => { this.saveLoading = false; });
        }
      });
    },

    deleteCategory() {
      this.deleteLoading = true;
      this.$axios.delete(`societyadmin/categories/${this.delete_id}`).then(response => {
        this.deleteLoading = false;
        const data = response.data;
        if (data.status) {
          this.deleteDialog = false;
          this.getCategories();
          this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: data.message });
        } else {
          this.showError(data.message);
        }
      }).catch(() => { this.deleteLoading = false; });
    },

    openAddDialog() {
      this.category = { id: null, name: "" };
      this.$validator.reset();
      this.serverSideErrors = {};
      this.addCategoryDialog = true;
    },

    openEditDialog(item) {
      this.category = { ...item };
      this.$validator.reset();
      this.serverSideErrors = {};
      this.addCategoryDialog = true;
    },

    confirmDeleteDialog(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    closeDialog() {
      this.addCategoryDialog = false;
      this.category = { id: null, name: "" };
      this.$validator.reset();
      this.serverSideErrors = {};
    },

    handleErrors(data) {
      if (data.errors) {
        this.serverSideErrors = {};
        data.errors.forEach(e => { this.serverSideErrors[e.field] = e.message; });
      } else {
        this.showError(data.message);
      }
    },

    showError(msg) {
      this.setAlert({ showAlert: true, variant: "red accent-3", timeout: "2000", message: msg });
    }
  },

  mounted() {
    this.user = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : {};
    
    // Initialize persistent filters
    this.initFilters('category');

    this.getCategories();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Cells ── */
.category-name-cell { display: flex; align-items: center; gap: 8px; }
.cat-name { font-size: 14px; font-weight: 700; color: var(--tx); }
.default-badge {
  font-size: 10px; font-weight: 800; color: #3B82F6; background: rgba(59, 130, 246, 0.1);
  padding: 2px 7px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.03em;
}


</style>
